// Turning a stored playlist row into something the KaziPeople player can play.
//
// A playlist row is a provider-neutral reference: the service parsed the pasted link and
// stored `provider` + `external_id` + `ref_kind` alongside the canonical URL. Playback used
// to re-parse the URL for a song id and silently DROPPED anything else — a pasted album
// link (no `?i=`) resolved to nothing, so "Play" fell through to opening Apple Music in a
// new tab. Pressing Play must never leave KaziPeople, so:
//
//   1. trust the stored `external_id` first (the service already did the parsing),
//   2. fall back to parsing the canonical URL (older rows predate external_id),
//   3. expand album / playlist references into their songs through the catalog, so the
//      whole thing plays in order instead of one track.
import type { MusicItem } from './types';

export type AppleRefKind = 'song' | 'album' | 'playlist';

export interface StoredTrackRef {
  provider?: string;
  external_id?: string;
  canonical_url: string;
  ref_kind?: string;
  title?: string;
  artist?: string;
}

export interface AppleRef {
  kind: AppleRefKind;
  id: string;
  title?: string;
  artist?: string;
}

const KINDS: Record<string, AppleRefKind> = { song: 'song', songs: 'song', album: 'album', albums: 'album', playlist: 'playlist', playlists: 'playlist' };

/** Parse an Apple Music web URL into a catalog reference. `?i=` always wins: an album URL
 *  carrying it points at one song on that album. */
export function parseAppleUrl(url: string): AppleRef | null {
  try {
    const u = new URL(url);
    if (!/(^|\.)music\.apple\.com$/.test(u.hostname)) return null;
    const song = u.searchParams.get('i');
    if (song) return { kind: 'song', id: song };
    const parts = u.pathname.split('/').filter(Boolean);
    for (let i = parts.length - 1; i >= 0; i--) {
      const kind = KINDS[parts[i]];
      // The id is the last path segment; Apple puts a human slug between kind and id.
      if (kind) {
        const id = parts[parts.length - 1];
        return id && id !== parts[i] ? { kind, id } : null;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/** The catalog reference for one stored row — the stored id first, the URL as the fallback. */
export function appleRef(t: StoredTrackRef): AppleRef | null {
  const meta = { title: t.title, artist: t.artist };
  const kind = t.ref_kind ? KINDS[t.ref_kind.toLowerCase()] : undefined;
  if (t.external_id && (!t.provider || t.provider === 'apple_music') && kind) {
    return { kind, id: t.external_id, ...meta };
  }
  const parsed = parseAppleUrl(t.canonical_url);
  return parsed ? { ...parsed, ...meta } : null;
}

const song = (id: string, title?: string, artist?: string): MusicItem =>
  ({ kind: 'song', id, provider: 'apple_music', title: title || '', artist });

export interface ResolveResult {
  /** Songs to queue, in playlist order. */
  songs: MusicItem[];
  /** Rows that yielded nothing playable (unsupported provider, or a dead reference). */
  unresolved: number;
  /** True when an album/playlist row could not be expanded because the catalog was unreachable. */
  partial: boolean;
}

/** Expand a list of stored rows into a flat, ordered song queue.
 *
 *  `expand` reaches the provider catalog (needs the listener's Apple Music session); when it
 *  is unavailable the album/playlist row is skipped rather than opening a browser tab. */
export async function resolveSongs(
  refs: StoredTrackRef[],
  expand?: { album: (id: string) => Promise<{ tracks: MusicItem[] }>; playlist: (id: string) => Promise<{ tracks: MusicItem[] }> },
): Promise<ResolveResult> {
  const songs: MusicItem[] = [];
  let unresolved = 0;
  let partial = false;
  for (const t of refs) {
    const ref = appleRef(t);
    if (!ref) { unresolved++; continue; }
    if (ref.kind === 'song') { songs.push(song(ref.id, ref.title, ref.artist)); continue; }
    if (!expand) { partial = true; unresolved++; continue; }
    try {
      const detail = ref.kind === 'album' ? await expand.album(ref.id) : await expand.playlist(ref.id);
      const tracks = (detail.tracks || []).filter((x) => x.kind === 'song');
      if (tracks.length) songs.push(...tracks);
      else { partial = true; unresolved++; }
    } catch {
      partial = true;
      unresolved++;
    }
  }
  return { songs, unresolved, partial };
}
